import { useEffect, useState } from 'react';
import { useOutletContext, Link } from 'react-router';
import {
  ExternalLink,
  Pause,
  Play,
  KeyRound,
  RefreshCw,
  Server,
  AlertTriangle,
  Plus,
  Repeat,
} from 'lucide-react';
import {
  Card,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  FormField,
  NotificationCard,
  StatusBadge,
  UniversalEmptyState,
  UniversalLoadingState,
  useToast,
} from '@sighthost/ui';
import {
  adminCustomerService,
  type AdminCustomerLink,
} from '@/domains/customers/services/adminCustomerService';
import { dunningPolicyMeta } from '@/domains/hosting/dunningPolicy';
import { hostingStateMeta, desiredReasonLabel } from '@/domains/hosting/hostingState';
import {
  adminHostingService,
  type WhmAccountAction,
  type DunningPolicy,
} from '@/domains/hosting/services/adminHostingService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import {
  adminPlanChangeService,
  type PlanChangeQuote,
} from '@/domains/hosting/services/adminPlanChangeService';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';
import type { CustomerDetailContext } from './customerDetailTypes';
import { AddHostingModal } from './AddHostingModal';
import { formatZAR } from '@/shared/utils/format';

const PLAN_OPTIONS = [
  { slug: 'entry-monthly', label: 'SightHost Entry (R 79/mo)' },
  { slug: 'basic-monthly', label: 'SightHost Basic (R 129/mo)' },
  { slug: 'super-monthly', label: 'SightHost Super (R 289/mo)' },
  { slug: 'ultimate-monthly', label: 'SightHost Ultimate (R 499/mo)' },
];

function monthlyAmount(link: AdminCustomerLink): number {
  const amt = link.service_plan_recurring_amount ?? 0;
  switch (link.service_plan_billing_cycle) {
    case 'annual':
      return amt / 12;
    case 'biennial':
      return amt / 24;
    default:
      return amt;
  }
}

interface ActionModalState {
  link: AdminCustomerLink;
  action: WhmAccountAction;
}

/**
 * Slice 3 (2026-09-10, gap F2): the dunning-policy editor. Until now the
 * policy was rendered read-only in three admin surfaces and could only be
 * changed by SQL — while any staff tier could PATCH it silently from the
 * browser. Now: admin-only, reasoned, audited, through the one sanctioned EF.
 */
function PolicyModal({
  link,
  onClose,
  onSaved,
}: {
  link: AdminCustomerLink;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [policy, setPolicy] = useState<DunningPolicy>((link.dunning_policy as DunningPolicy) ?? 'no_cancel');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options: { value: DunningPolicy; label: string; detail: string }[] = [
    { value: 'no_cancel', label: 'Never auto-terminate (default)', detail: 'May be suspended for non-payment; the account is never deleted automatically.' },
    { value: 'no_suspend', label: 'Never suspend', detail: 'Invoiced and chased like any other, but never taken offline. Intercompany / protected sites.' },
    { value: 'standard', label: 'Standard (may terminate)', detail: 'Full ladder incl. auto-termination — currently disarmed platform-wide (removeacct cap 0), but this is key 1 of that two-key act.' },
  ];

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await adminHostingService.setDunningPolicy({
        customer_link_id: link.id,
        dunning_policy: policy,
        reason: reason.trim(),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Change dunning policy"
      description={
        <>
          <code className="text-xs">{link.cpanel_username}</code> · currently{' '}
          <strong>{dunningPolicyMeta(link.dunning_policy).label}</strong>
        </>
      }
    >
      <ModalBody>
        <div className="space-y-4">
          <FormField label="Policy" htmlFor="dunning_policy" required>
            <select
              id="dunning_policy"
              value={policy}
              onChange={(e) => setPolicy(e.target.value as DunningPolicy)}
              disabled={submitting}
              className="h-9 w-full rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {options.find((o) => o.value === policy)?.detail}
            </p>
          </FormField>
          <FormField label="Reason (required for audit)" htmlFor="policy_reason" required helper="Recorded on audit_log with your name; the account is re-evaluated immediately.">
            <Input
              id="policy_reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              placeholder="e.g. intercompany site; owner decision 2026-09-10"
            />
          </FormField>
          {error && <NotificationCard tone="danger" title="Could not change policy" body={error} />}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={submit}
          disabled={submitting || reason.trim().length === 0 || policy === (link.dunning_policy ?? 'no_cancel')}
          loading={submitting}
        >
          {submitting ? 'Saving…' : 'Save policy'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default function CustomerHostingTab() {
  const { customer } = useOutletContext<CustomerDetailContext>();
  const { toast } = useToast();
  const [links, setLinks] = useState<AdminCustomerLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [actionModal, setActionModal] = useState<ActionModalState | null>(null);
  const [policyLink, setPolicyLink] = useState<AdminCustomerLink | null>(null);
  const { isAdmin } = useStaffRole();
  const [planChangeLink, setPlanChangeLink] = useState<AdminCustomerLink | null>(null);
  const [addHostingOpen, setAddHostingOpen] = useState(false);

  function refreshLinks() {
    adminCustomerService
      .listLinks(customer.customer_id)
      .then((data) => setLinks(data))
      .catch(() => {});
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminCustomerService
      .listLinks(customer.customer_id)
      .then((data) => {
        if (!cancelled) {
          setLinks(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customer.customer_id]);

  async function openCpanelSso(link: AdminCustomerLink) {
    try {
      const res = await adminHostingService.getCpanelSsoUrl(link.cpanel_username);
      window.open(res.url, '_blank', 'noopener,noreferrer');
      toast({
        tone: 'info',
        title: 'cPanel session opened',
        body: 'A one-time URL was opened in a new tab.',
      });
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not open cPanel',
        body: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (loading) return <UniversalLoadingState variant="cards" count={2} />;
  if (error) {
    return (
      <NotificationCard
        tone="danger"
        title="Could not load hosting accounts"
        body={error.message}
      />
    );
  }

  const addHostingButton = (
    <Button variant="primary" size="sm" onClick={() => setAddHostingOpen(true)}>
      <Plus className="h-4 w-4" />
      Add hosting
    </Button>
  );

  if (links.length === 0) {
    return (
      <>
        <div className="mb-3 flex items-center justify-end">{addHostingButton}</div>
        <Card interactive={false}>
          <UniversalEmptyState
            icon={Server}
            title="No hosting accounts linked"
            description="Use Add hosting to issue an invoice for a new plan. The customer pays via PayFast; provisioning runs automatically once payment clears. (For pre-existing cPanel accounts, use Import existing from the customers list.)"
            action={addHostingButton}
          />
        </Card>
        {addHostingOpen && (
          <AddHostingModal
            customerId={customer.customer_id}
            customerEmail={customer.email ?? ''}
            customerName={customer.full_name}
            onClose={() => setAddHostingOpen(false)}
            onSubmitted={() => {
              setAddHostingOpen(false);
              refreshLinks();
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="mb-3 flex items-center justify-end">{addHostingButton}</div>
      <div className="space-y-4">
        {links.map((link) => (
          <HostingCard
            key={link.id}
            link={link}
            onOpenSso={() => openCpanelSso(link)}
            onSetPolicy={isAdmin ? () => setPolicyLink(link) : undefined}
            onAction={(action: HostingCardAction) => {
              if (action === 'change_plan') {
                setPlanChangeLink(link);
              } else {
                setActionModal({ link, action });
              }
            }}
          />
        ))}
      </div>

      {policyLink && (
        <PolicyModal
          link={policyLink}
          onClose={() => setPolicyLink(null)}
          onSaved={() => {
            setPolicyLink(null);
            toast({ tone: 'success', title: 'Policy changed', body: 'Recorded on the audit log; the account was re-evaluated.' });
            refreshLinks();
          }}
        />
      )}

      {actionModal && (
        <ActionConfirmModal
          state={actionModal}
          onClose={() => setActionModal(null)}
          onSubmitted={() => {
            setActionModal(null);
            toast({
              tone: 'success',
              title: 'Job queued',
              body: 'The provisioning runner will pick this up within ~1 minute.',
            });
          }}
        />
      )}

      {addHostingOpen && (
        <AddHostingModal
          customerId={customer.customer_id}
          customerEmail={customer.email ?? ''}
          customerName={customer.full_name}
          onClose={() => setAddHostingOpen(false)}
          onSubmitted={() => {
            setAddHostingOpen(false);
            refreshLinks();
          }}
        />
      )}

      {planChangeLink && (
        <ChangePlanModal
          link={planChangeLink}
          onClose={() => setPlanChangeLink(null)}
          onSubmitted={(result) => {
            setPlanChangeLink(null);
            refreshLinks();
            if (result.kind === 'upgrade') {
              toast({
                tone: 'success',
                title: 'Upgrade invoice issued',
                body: 'Customer has been emailed. WHM package changes once payment clears.',
              });
            } else if (result.kind === 'downgrade') {
              toast({
                tone: 'success',
                title: 'Downgrade applied',
                body: 'WHM package change job enqueued; runs within ~1 minute.',
              });
            } else {
              toast({
                tone: 'success',
                title: 'Plan changed',
                body: 'WHM package re-aligned.',
              });
            }
          }}
        />
      )}
    </>
  );
}

// UI-level action union — includes the 'change_plan' button which routes to
// the saga modal (not the WhmAccountAction enqueue path; AD-17).
type HostingCardAction = WhmAccountAction | 'change_plan';

function HostingCard({
  link,
  onOpenSso,
  onAction,
  onSetPolicy,
}: {
  link: AdminCustomerLink;
  onOpenSso: () => Promise<void>;
  onAction: (action: HostingCardAction) => void;
  /** Present only for admins (Slice 3, gap F2). */
  onSetPolicy?: () => void;
}) {
  const monthly = monthlyAmount(link);
  const [ssoBusy, setSsoBusy] = useState(false);

  async function handleOpenSso() {
    if (ssoBusy) return;
    setSsoBusy(true);
    try {
      await onOpenSso();
    } finally {
      setSsoBusy(false);
    }
  }

  return (
    <Card interactive={false}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {link.primary_domain ?? link.cpanel_username}
            </h3>
            {/* F-12. Beside the account's own name, because "can this be
                automatically destroyed" is a property of THIS account and the
                answer differs per row. It lived only in the database until now. */}
            {(() => {
              const meta = dunningPolicyMeta(link.dunning_policy);
              return (
                <span title={meta.detail} className="inline-flex items-center gap-1">
                  <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
                  {onSetPolicy && (
                    <button
                      type="button"
                      onClick={onSetPolicy}
                      className="text-xs text-primary-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      change
                    </button>
                  )}
                </span>
              );
            })()}
            {/* Slice 2 (2026-09-10): the OBSERVED state — what WHM last told
                us — beside the policy. Never inferred from invoice state. */}
            {(() => {
              const obs = hostingStateMeta(link.hosting_state);
              const wants = link.hosting_desired_state ?? '—';
              const disagree =
                link.hosting_state !== 'unknown' && link.hosting_state !== null &&
                link.hosting_state !== 'terminated' && link.hosting_state !== wants;
              return (
                <span
                  title={`WHM: ${obs.detail}${link.hosting_state_observed_at ? ` Seen ${new Date(link.hosting_state_observed_at).toLocaleString('en-ZA')}.` : ''} Books want: ${wants} (${desiredReasonLabel(link.hosting_desired_reason_code)}).`}
                  className="inline-flex items-center gap-1"
                >
                  <StatusBadge tone={obs.tone}>WHM: {obs.label}</StatusBadge>
                  {disagree && <StatusBadge tone="overdue">Books want {wants}</StatusBadge>}
                </span>
              );
            })()}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            cPanel <code className="rounded bg-muted px-1">{link.cpanel_username}</code>
            {' '}on {link.whm_server}
          </p>
        </div>
        <div className="text-right text-sm">
          <p className="font-semibold text-foreground">
            {link.service_plan_name ?? 'No plan'}
          </p>
          {monthly > 0 && (
            <p className="text-xs text-muted-foreground">
              {formatZAR(monthly)}/mo
            </p>
          )}
          {link.recurring_enabled && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-success">
              <Repeat className="size-3" aria-hidden />
              Auto-pay active
            </p>
          )}
        </div>
      </header>
      <div className="grid grid-cols-1 gap-3 px-6 py-4 text-sm sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Current period
          </p>
          <p className="text-foreground">
            {link.current_period_start
              ? `${link.current_period_start} → ${link.current_period_end ?? '—'}`
              : 'Not started'}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {link.recurring_enabled ? 'Next auto-charge' : 'Next billing'}
          </p>
          <p className="text-foreground">
            {link.next_billing_date ?? 'TBD'}
          </p>
          {link.recurring_enabled && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Driven by PayFast Recurring — billing-cron skips this row.
            </p>
          )}
        </div>
      </div>
      <footer className="flex flex-wrap gap-2 border-t border-border bg-muted px-6 py-3">
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpenSso}
          loading={ssoBusy}
          disabled={ssoBusy}
        >
          <ExternalLink className="h-4 w-4" />
          Open cPanel SSO
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAction('suspend')}
        >
          <Pause className="h-4 w-4" />
          Suspend
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAction('unsuspend')}
        >
          <Play className="h-4 w-4" />
          Unsuspend
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAction('change_plan')}
        >
          <RefreshCw className="h-4 w-4" />
          Change plan
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAction('change_password')}
        >
          <KeyRound className="h-4 w-4" />
          Reset cPanel pwd
        </Button>
      </footer>
    </Card>
  );
}

function ActionConfirmModal({
  state,
  onClose,
  onSubmitted,
}: {
  state: ActionModalState;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [needsOverride, setNeedsOverride] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDestructive =
    state.action === 'suspend' || state.action === 'terminate';
  // Slice 3 (2026-09-10): suspend AND unsuspend carry a mandatory reason —
  // it is audited and, from Slice 4, told to the customer.
  const requiresReason = isDestructive || state.action === 'unsuspend';
  const requiresPassword = state.action === 'change_password';

  const titleMap: Record<WhmAccountAction, string> = {
    suspend: 'Suspend cPanel account',
    unsuspend: 'Unsuspend cPanel account',
    terminate: 'Terminate cPanel account',
    change_password: 'Reset cPanel password',
  };

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await adminHostingService.enqueueAction({
        action: state.action,
        cpanel_username: state.link.cpanel_username,
        reason: reason.trim() || undefined,
        new_password: requiresPassword ? newPassword : undefined,
        override_reason: overrideReason.trim() || undefined,
      });
      onSubmitted();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Slice 3 (G2): the EF refuses to unsuspend while a suspended invoice is
      // still open with no arrangement. Surface the override field instead of
      // a dead end; the override reason is audited.
      if (state.action === 'unsuspend' && /still suspended and unpaid|open_suspended_invoice/i.test(msg)) {
        setNeedsOverride(true);
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={titleMap[state.action]}
      description={
        <>
          <code className="text-xs">{state.link.cpanel_username}</code>{' '}
          on {state.link.whm_server}
        </>
      }
    >
      <ModalBody>
        <div className="space-y-4">
          {isDestructive && (
            <NotificationCard
              tone="warning"
              title="Confirm before submitting"
              body={
                <>
                  This action affects the customer's hosting service. A
                  provisioning_job will be enqueued and runs within ~1 minute.{' '}
                  <Link
                    to="/admin/ops/provisioning"
                    className="underline underline-offset-2"
                  >
                    Track progress
                  </Link>
                  .
                </>
              }
            />
          )}

          {requiresPassword && (
            <FormField
              label="New cPanel password"
              htmlFor="new_password"
              required
              helper="Customer should change this after sign-in. Never logged."
            >
              <Input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={submitting}
                autoComplete="new-password"
              />
            </FormField>
          )}

          <FormField
            label={requiresReason ? 'Reason (required for audit)' : 'Reason (optional)'}
            htmlFor="reason"
            required={requiresReason}
            helper="Recorded on audit_log."
          >
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              placeholder="e.g. customer request, non-payment, abuse"
            />
          </FormField>

          {needsOverride && (
            <FormField
              label="Override reason (required — an invoice is still suspended and unpaid)"
              htmlFor="override_reason"
              required
              helper="Prefer recording a payment arrangement instead; it lifts the suspension and holds the ladder. An override is audited against your name."
            >
              <Input
                id="override_reason"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                disabled={submitting}
                placeholder="e.g. paid by EFT, proof received by phone"
              />
            </FormField>
          )}

          {error && (
            <NotificationCard tone="danger" title="Could not enqueue" body={error} />
          )}

          {state.action === 'terminate' && (
            <div className="flex items-start gap-2 rounded border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Termination removes the cPanel account permanently. Files,
                databases, and email are deleted from the WHM server.
              </p>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={
            submitting ||
            (requiresReason && reason.trim().length === 0) ||
            (needsOverride && overrideReason.trim().length === 0) ||
            (requiresPassword && newPassword.length === 0)
          }
          loading={submitting}
        >
          {submitting ? 'Enqueueing…' : 'Enqueue job'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================================
// ChangePlanModal — two-step flow that calls plan-change-quote then
// plan-change-confirm. Replaces the old enqueueAction({action:'change_plan'})
// path per AD-17 (the saga is the only path).
// ============================================================================

function ChangePlanModal({
  link,
  onClose,
  onSubmitted,
}: {
  link: AdminCustomerLink;
  onClose: () => void;
  onSubmitted: (result: { kind: 'upgrade' | 'downgrade' | 'no_change' }) => void;
}) {
  const [newPlanSlug, setNewPlanSlug] = useState<string>(() => {
    const candidates = PLAN_OPTIONS.filter(
      (p) => p.slug !== link.service_plan_slug
    );
    return candidates[0]?.slug ?? PLAN_OPTIONS[0]?.slug ?? '';
  });
  const [notes, setNotes] = useState('');
  const [quote, setQuote] = useState<PlanChangeQuote | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve new plan id from slug via the quote response — quote() needs
  // new_plan_id, but we only know the slug from the dropdown. Workaround:
  // call quote with a slug-to-id map sourced from a tiny inline lookup.
  // A nicer fix is a service_plan listing endpoint; deferred.
  const [planIdsBySlug, setPlanIdsBySlug] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    import('@/shared/lib/supabase').then(({ supabase }) => {
      supabase
        .from('service_plan')
        .select('id, slug')
        .eq('is_active', true)
        .then(({ data }) => {
          if (cancelled || !data) return;
          const map: Record<string, string> = {};
          for (const row of data as { id: string; slug: string }[]) {
            map[row.slug] = row.id;
          }
          setPlanIdsBySlug(map);
        });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function getQuote() {
    setLoadingQuote(true);
    setError(null);
    setQuote(null);
    try {
      const newPlanId = planIdsBySlug[newPlanSlug];
      if (!newPlanId) throw new Error('Plan not loaded — try again');
      const result = await adminPlanChangeService.quote({
        customer_link_id: link.id,
        new_plan_id: newPlanId,
      });
      setQuote(result);
    } catch (err) {
      if (err instanceof EdgeFunctionError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoadingQuote(false);
    }
  }

  async function confirmChange() {
    if (!quote) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await adminPlanChangeService.confirm({
        customer_link_id: link.id,
        new_plan_id: quote.new_plan.id,
        effective_date: quote.effective_date,
        notes: notes.trim() || undefined,
      });
      onSubmitted({ kind: result.kind });
    } catch (err) {
      if (err instanceof EdgeFunctionError) {
        if (err.code === 'plan_change_already_pending') {
          setError(
            'A plan change is already pending for this account. Wait for it to complete or check the Activity tab.'
          );
        } else if (err.code === 'reseller_at_disk_cap') {
          // Sprint C C-4 — confirm-time refusal when oversell ratio would breach.
          setError(
            'This upgrade would breach the reseller disk oversell ratio. Raise system_setting.reseller_max_oversell_ratio first if you want to allow it (admin: SQL UPDATE on system_setting), or pick a smaller target plan.'
          );
        } else if (err.code === 'reseller_at_account_cap' || err.code === 'reseller_at_cap') {
          setError(
            "The reseller account ceiling has been reached. Upgrade the WHM reseller tier (or unlink an inactive customer_link) before retrying."
          );
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const planChoices = PLAN_OPTIONS.filter(
    (p) => p.slug !== link.service_plan_slug
  );

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Change hosting plan"
      description={
        <>
          <code className="text-xs">{link.cpanel_username}</code>{' '}
          on {link.whm_server} · currently{' '}
          <strong>{link.service_plan_name ?? '—'}</strong>
        </>
      }
    >
      <ModalBody>
        <div className="space-y-4">
          {!quote && (
            <FormField label="New plan" htmlFor="new_plan_slug" required>
              <select
                id="new_plan_slug"
                value={newPlanSlug}
                onChange={(e) => setNewPlanSlug(e.target.value)}
                disabled={loadingQuote || submitting}
                className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {planChoices.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.label}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          {quote && (
            <div className="space-y-3">
              <NotificationCard
                tone={
                  quote.kind === 'upgrade'
                    ? 'info'
                    : quote.kind === 'downgrade'
                    ? 'warning'
                    : 'info'
                }
                title={
                  quote.kind === 'upgrade'
                    ? 'Upgrade — invoice will be issued now'
                    : quote.kind === 'downgrade'
                    ? 'Downgrade — applies immediately, no refund (AD-16)'
                    : 'No price difference — WHM realignment only'
                }
                body={quote.message}
              />

              <div className="rounded border border-border bg-muted px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Current plan</span>
                  <span className="font-medium">
                    {quote.current_plan.name} ·{' '}
                    {formatZAR(quote.current_plan.recurring_amount)}/
                    {quote.current_plan.billing_cycle.replace('ly', '')}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-muted-foreground">New plan</span>
                  <span className="font-medium">
                    {quote.new_plan.name} ·{' '}
                    {formatZAR(quote.new_plan.recurring_amount)}/
                    {quote.new_plan.billing_cycle.replace('ly', '')}
                  </span>
                </div>
                <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                  Period {quote.period.start} → {quote.period.end} ·{' '}
                  {quote.period.days_remaining}/{quote.period.days_total} days
                  remaining
                </div>
              </div>

              {quote.kind === 'upgrade' && (
                <div className="rounded border border-border bg-muted/50 px-4 py-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Charge for new plan ({quote.period.days_remaining} days)
                    </span>
                    <span className="tabular-nums">
                      {formatZAR(quote.proration.charge_for_new_period)}
                    </span>
                  </div>
                  <div className="flex justify-between text-success-dark">
                    <span>Credit for unused current plan</span>
                    <span className="tabular-nums">
                      −{formatZAR(quote.proration.credit_for_unused_current)}
                    </span>
                  </div>
                  <div className="mt-1 flex justify-between border-t border-border pt-1 text-base font-semibold text-foreground">
                    <span>Net charge</span>
                    <span className="tabular-nums">
                      {formatZAR(quote.proration.net_charge)}
                    </span>
                  </div>
                </div>
              )}

              <FormField
                label="Notes (optional)"
                htmlFor="plan_change_notes"
                helper="Recorded on plan_change_request and audit_log."
              >
                <Input
                  id="plan_change_notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={submitting}
                  placeholder="e.g. customer requested via email 2026-05-03"
                  maxLength={500}
                />
              </FormField>

              {/* Sprint C C-4 — quote-time capacity warning. Confirm EF will refuse
                  if the breach holds, but surfacing it here lets staff bail out
                  before clicking confirm. */}
              {quote.capacity_warning && (
                <NotificationCard
                  tone="warning"
                  title="Reseller capacity warning"
                  body={
                    quote.capacity_warning.reason === 'disk_oversell_breach'
                      ? "This upgrade would push the reseller disk oversell ratio past its limit. The confirm step will refuse until system_setting.reseller_max_oversell_ratio is raised — or pick a smaller target plan."
                      : "This change would breach the reseller capacity limits. The confirm step will refuse until the configured limits are raised."
                  }
                />
              )}
            </div>
          )}

          {error && (
            <NotificationCard
              tone="warning"
              title="Could not process plan change"
              body={error}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="outline"
          onClick={onClose}
          disabled={loadingQuote || submitting}
        >
          Cancel
        </Button>
        {!quote ? (
          <Button
            onClick={getQuote}
            loading={loadingQuote}
            disabled={loadingQuote || !planIdsBySlug[newPlanSlug]}
          >
            <RefreshCw className="h-4 w-4" />
            {loadingQuote ? 'Quoting…' : 'Get quote'}
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => setQuote(null)} disabled={submitting}>
              Back
            </Button>
            <Button onClick={confirmChange} loading={submitting} disabled={submitting}>
              {quote.kind === 'upgrade'
                ? 'Issue invoice'
                : quote.kind === 'downgrade'
                ? 'Apply downgrade'
                : 'Realign WHM'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
